import React, { useState } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';

import StarRating from '../../../../library/star-rating/star-rating';
import {
  CheckCircleSolidIcon,
  MapMarkerAltSolidIcon,
  ThumbsDownSolidIcon,
  ThumbsUpSolidIcon,
  TimesCircleSolidIcon
} from '../../../../shared/icons/icons';
import RatingGauge from '../../../rating-gauge/rating-gauge';

import './review.scss';

function Review(props) {
  const {
    answers,
    city,
    comments,
    date,
    details,
    heading,
    helpfulCount,
    notHelpfulCount,
    notRecommendedMessage,
    onHelpfulClick,
    rating,
    recommendedMessage,
    reviewerName,
    subHeading,
    state,
    id,
    isRecommended,
    isFeedbackSubmitted
  } = props;
  const [hasSubmittedFeedback, setHasSubmittedFeedback] =
    useState(isFeedbackSubmitted);

  function submitHelpfulFeedback() {
    setHasSubmittedFeedback(true);
    if (onHelpfulClick) {
      onHelpfulClick(id, true);
    }
  }

  function submitNotHelpfulFeedback() {
    setHasSubmittedFeedback(true);
    if (onHelpfulClick) {
      onHelpfulClick(id, false);
    }
  }

  return (
    <div styleName="container">
      <div styleName="reviewer">
        <div styleName="heading">
          {heading}
          <br />
          {subHeading && <span styleName="sub-heading">{subHeading}</span>}
        </div>

        <div styleName="reviewer-rating">
          <StarRating rating={rating} />
        </div>

        {typeof reviewerName === 'string' && (
          <span styleName="reviewer-name">{reviewerName || 'Anonymous'}</span>
        )}

        {city && state && (
          <span>
            <MapMarkerAltSolidIcon />
            &nbsp;{city}, {state}
          </span>
        )}
      </div>

      <div styleName="details">
        <div styleName="recommendation">
          {isRecommended ? (
            <span styleName="recommended">
              <CheckCircleSolidIcon styleName="recommend-icon" />
              {recommendedMessage && (
                <span styleName="recommend-message">{recommendedMessage}</span>
              )}
            </span>
          ) : (
            <span styleName="not-recommended">
              <TimesCircleSolidIcon styleName="recommend-icon" />
              {notRecommendedMessage && (
                <span styleName="recommend-message">
                  {notRecommendedMessage}
                </span>
              )}
            </span>
          )}
          <span styleName="date">{format(date || '', 'M/D/YYYY')}</span>
        </div>

        {Array.isArray(details) && (
          <ul styleName="details-list">
            {details.map((detail, idx) => (
              <li key={`dl-${idx}`} styleName="details-list-item">
                <span styleName="detail-label">{detail?.label}:</span>
                {detail?.value}
              </li>
            ))}
          </ul>
        )}

        <div styleName="rating-breakdown">
          {Array.isArray(answers) &&
            answers.map((answer, idx) => (
              <RatingGauge
                key={`rba-${idx}`}
                label={answer.label}
                rating={answer.rating}
                styleName="rating-gauge"
              />
            ))}
        </div>

        <p
          dangerouslySetInnerHTML={{
            __html: comments
          }}
          styleName="comments"
        />

        <div styleName="helpful">
          {hasSubmittedFeedback ? (
            <span styleName="helpful-message-thanks">
              Thank You for your feedback!
            </span>
          ) : (
            <span styleName="helpful-message">Was this review helpful?</span>
          )}

          {!hasSubmittedFeedback && (
            <div className="helpful-feedback">
              <button
                aria-label="Submit feedback review helpful"
                onClick={submitHelpfulFeedback}
                styleName="feedback-button"
                type="button"
              >
                <ThumbsUpSolidIcon styleName="feedback-icon" />
                {helpfulCount}
              </button>

              <button
                aria-label="Submit feedback review not helpful"
                onClick={submitNotHelpfulFeedback}
                styleName="feedback-button"
                type="button"
              >
                <ThumbsDownSolidIcon styleName="feedback-icon" />
                {notHelpfulCount}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

Review.propTypes = {
  answers: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      rating: PropTypes.number
    })
  ),
  city: PropTypes.string,
  comments: PropTypes.string,
  date: PropTypes.string,
  details: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string
    })
  ),
  feedbackSubmitted: PropTypes.bool,
  heading: PropTypes.string,
  helpfulCount: PropTypes.number,
  id: PropTypes.string,
  isFeedbackSubmitted: PropTypes.bool,
  isRecommended: PropTypes.bool,
  notHelpfulCount: PropTypes.number,
  notRecommendedMessage: PropTypes.string,
  onHelpfulClick: PropTypes.func,
  rating: PropTypes.number,
  recommendedMessage: PropTypes.string,
  reviewerName: PropTypes.string,
  state: PropTypes.string,
  subHeading: PropTypes.string
};

export default Review;
